import { EmailItem } from '../../components/List/Email/EmailList'
import { IObjectWithKey, ISelection } from '@fluentui/react'
import useAxios from '../../hooks/useAxios'
import useHost from '../useHost'
import { normalizeAxiosError, NormalizedError } from '../../reducers/errorReducer'
import { useState } from 'react'
import useEmailFilingHandler from './useEmailFilingHandler'
import { AxiosResponse } from 'axios'
import { mapSelectableEmail } from '../../components/Dialog/ProcessEmail/FormContent'
import { appInsightsReact } from '../../pages/App'
import { SeverityLevel } from '@microsoft/applicationinsights-common'

export type SelectableEmail = EmailItem & IObjectWithKey

export type FilterReferences = (references: string[]) => string[]

export const PROCESS_EMAIL_TIMEOUT_LIMIT = 1000
export const PROCESS_EMAIL_TIMEOUT_LENGTH = 250

export default function useProcessEmail() {
    const axios = useAxios()
    const host = useHost()
    const [, setGlobalErrors] = useState<NormalizedError[]>()
    const handler = useEmailFilingHandler()

    const processEmail = (
        email: string,
        notificationEmail: string,
        selectedEmail: SelectableEmail,
        filterReferences: FilterReferences,
    ) => {
        return axios.post('/email/file-suggest', [mapSelectableEmail(selectedEmail)]).then(
            result =>
                handler.dispatch(selectedEmail.id, {
                    baseURL: process.env.INBOXER_HOST,
                    url: `/api/file-message/`,
                    data: {
                        messageId: selectedEmail.id,
                        userEmail: email,
                        notificationEmail: notificationEmail,
                        hostKey: host.RowKey,
                        references: filterReferences(result.data.map(suggestion => suggestion.reference)),
                    },
                    mapDispatchResponse: (response: AxiosResponse<void>) => response.data,
                }),
            error => {
                appInsightsReact.trackException({
                    exception: error,
                    severityLevel: SeverityLevel.Error,
                    properties: { reactComponent: 'useProcessEmail', emailId: selectedEmail?.id },
                })
            },
        )
    }

    const processSelection = (
        email: string,
        notificationEmail: string,
        selection: ISelection<SelectableEmail>,
        filterReferences: FilterReferences,
        init: () => void,
        exit: () => void,
    ) => {
        selection.setChangeEvents(false)
        init()

        return Promise.allSettled(
            selection
                .getSelection()
                .map(selectedEmail => processEmail(email, notificationEmail, selectedEmail, filterReferences)),
        )
            .catch(error => setGlobalErrors([normalizeAxiosError(error)]))
            .finally(() => {
                const timeout = PROCESS_EMAIL_TIMEOUT_LENGTH * selection.getSelectedCount()
                // Short timeout to allow for Exchange to process the changes.
                setTimeout(
                    () => {
                        exit()
                        selection.setChangeEvents(true)
                    },
                    timeout < PROCESS_EMAIL_TIMEOUT_LIMIT ? timeout : PROCESS_EMAIL_TIMEOUT_LENGTH,
                )
            })
    }

    return { processSelection }
}
